<script setup lang="ts">
import { computed } from 'vue'

import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'

export type ChipColor = 'primary' | 'neutral' | 'dark' | 'success' | 'warning' | 'danger' | 'info'
export type ChipVariant = 'solid' | 'outline' | 'tonal'
export type ChipSize = 'small' | 'medium'

interface Props {
  /** Text content. Overridden by default slot. */
  label?: string
  /** Semantic color. */
  color?: ChipColor
  /**
   * Visual appearance. `tonal` — translucent fill + token-700 text
   * (the global "status pill" treatment). `solid` — filled, high-emphasis.
   * `outline` — border-only.
   */
  variant?: ChipVariant
  /** Sizing preset. */
  size?: ChipSize
  /** Optional leading dot. Pairs with `tonal` for status feedback. */
  dot?: boolean
  /** Optional leading icon (PrimeIcons name, no `pi-` prefix). */
  icon?: PiIconName
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  color: 'neutral',
  variant: 'solid',
  size: 'medium',
  dot: false,
})

const chipClasses = computed(() => [
  'chip',
  `chip--color-${props.color}`,
  `chip--variant-${props.variant}`,
  `chip--size-${props.size}`,
])
</script>

<template>
  <span :class="chipClasses">
    <span v-if="dot" :class="['chip__dot', `chip__dot--${color}`]" aria-hidden="true" />
    <PiIcon v-if="icon" :name="icon" class="chip__icon" />
    <slot>{{ label }}</slot>
  </span>
</template>

<style scoped>
.chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  border-radius: 999px;
  line-height: 1;
  font-weight: 500;
  white-space: nowrap;
  vertical-align: middle;
}

/* ── Sizes ──
   Standardised heights so every chip on every screen lines up.
*/
.chip--size-small {
  height: 22px;
  padding: 0 0.625rem;
  font-size: 11px;
  letter-spacing: 0.01em;
}

.chip--size-medium {
  height: 26px;
  padding: 0 0.75rem;
  font-size: 12px;
}

/* ── Leading dot ── */
.chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chip__icon {
  font-size: 11px;
  line-height: 1;
  flex-shrink: 0;
}

/* ── Solid variants — color sets bg + text ── */
.chip--color-primary.chip--variant-solid {
  background: var(--p-primary-500);
  color: var(--p-primary-contrast-color);
}

.chip--color-neutral.chip--variant-solid {
  background: var(--p-surface-200);
  color: var(--p-text-color);
}

.chip--color-dark.chip--variant-solid {
  background: var(--p-sliceal-chip-dark-navy);
  color: var(--p-primary-contrast-color);
}

.chip--color-success.chip--variant-solid {
  background: var(--p-success-700);
  color: var(--p-primary-contrast-color);
}

.chip--color-warning.chip--variant-solid {
  background: var(--p-warning-600);
  color: var(--p-primary-contrast-color);
}

.chip--color-danger.chip--variant-solid {
  background: var(--p-danger-500);
  color: var(--p-primary-contrast-color);
}

.chip--color-info.chip--variant-solid {
  background: var(--p-info-500);
  color: var(--p-primary-contrast-color);
}

/* ── Outline variants ── */
.chip--color-primary.chip--variant-outline {
  background: transparent;
  color: var(--p-primary-600);
  border: 1px solid var(--p-primary-400);
}

.chip--color-neutral.chip--variant-outline {
  background: transparent;
  color: var(--p-text-color);
  border: 1px solid var(--p-content-border-color);
}

.chip--color-dark.chip--variant-outline {
  background: transparent;
  color: var(--p-sliceal-chip-dark-navy);
  border: 1px solid var(--p-sliceal-chip-dark-navy);
}

.chip--color-success.chip--variant-outline {
  background: var(--p-success-50);
  color: var(--p-success-600);
  border: 1px solid var(--p-success-200);
}

.chip--color-warning.chip--variant-outline {
  background: transparent;
  color: var(--p-warning-700);
  border: 1px solid var(--p-warning-400);
}

.chip--color-danger.chip--variant-outline {
  background: transparent;
  color: var(--p-danger-600);
  border: 1px solid var(--p-danger-400);
}

.chip--color-info.chip--variant-outline {
  background: transparent;
  color: var(--p-info-600);
  border: 1px solid var(--p-info-400);
}

/* ── Tonal variants — translucent fill + token-700 text ──
   The premium status-pill treatment. Reads as institutional status
   feedback (Linear / Ramp / Stripe Dashboard) rather than a consumer
   solid fill. Use for status, MFA, risk, queue stages.
*/
.chip--color-primary.chip--variant-tonal {
  background: rgba(103, 85, 251, 0.1);
  color: var(--p-primary-700);
}

.chip--color-neutral.chip--variant-tonal {
  background: rgba(148, 163, 184, 0.12);
  color: var(--p-text-color);
}

.chip--color-success.chip--variant-tonal {
  background: rgba(34, 197, 94, 0.1);
  color: var(--p-success-700);
}

.chip--color-warning.chip--variant-tonal {
  background: rgba(245, 158, 11, 0.1);
  color: var(--p-warning-800);
}

.chip--color-danger.chip--variant-tonal {
  background: rgba(240, 68, 68, 0.1);
  color: var(--p-danger-700);
}

.chip--color-info.chip--variant-tonal {
  background: rgba(61, 137, 245, 0.1);
  color: var(--p-info-700);
}

.chip--color-dark.chip--variant-tonal {
  background: rgba(15, 23, 42, 0.08);
  color: var(--p-text-color);
}

/* ── Dot colours (paired with tonal variant) ── */
.chip__dot--primary {
  background: var(--p-primary-500);
}
.chip__dot--neutral {
  background: var(--p-surface-400);
}
.chip__dot--success {
  background: var(--p-success-500);
}
.chip__dot--warning {
  background: var(--p-warning-500);
}
.chip__dot--danger {
  background: var(--p-danger-500);
}
.chip__dot--info {
  background: var(--p-info-500);
}
.chip__dot--dark {
  background: var(--p-text-color);
}
</style>
